import { Luggage, LuggageIcon, LucideLuggage, LucideBaggageClaim } from "lucide-react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { driverApi, getErrorMessage, tripsApi } from "../../../api";
import InfoCard from "../../../components/InfoCard";
import RouteConnector from "../../../components/RouteConnector";
import Toggle from "../../../components/Toggle/Toggle";
import { APIContext } from "../../../connector";
import { ws_url } from "../../../constants";
import { useToast } from "../../../hooks/useToast";
import "../../../index.scss";
import backArrow from "../../../static/images/backArrow.svg";
import { subscribeToPush } from "../../../utils/pushHelper";
import useReliableWebSocket from "../../../utils/useReliableWebSocket";
import classes from "./DriverOrders.module.scss";

const infoCardTitle = "Pick Up Now";

const DriverOrders = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { openToast } = useToast();

  const [orders, setOrders] = useState(null);
  const [pendingTripId, setPendingTripId] = useState(null); // UI: which card was clicked
  const [priceAdjustments, setPriceAdjustments] = useState({});

  // const { sendJsonMessage, lastJsonMessage, userData } = useContext(APIContext);
  const { userData } = useContext(APIContext);

  const [receiveNotifications, setReceiveNotifications] = useState(userData?.driver_info?.receive_notifications === true);

  const tripUrl = `${ws_url}/ws/trip/${tripId}/`;
  const { lastJsonMessage: tripMessage, sendJsonMessage: sendTripMessage } = useReliableWebSocket(tripUrl, {
    share: true,
    shouldReconnect: () => false,
    retryOnError: false,
  });

  const data = {
    gender: userData?.gender || "Unknown",
    credit: userData?.driver_info?.credit_balance || 0,
  };

  const reloadOrders = useCallback(async () => {
    try {
      const data = await tripsApi.getAvailableTrips();

      if (!Array.isArray(data)) {
        console.error("Unexpected data format:", data);
        openToast("Something went wrong...", 5000, "danger");
        setOrders([]);
        return;
      }

      setOrders(data);

      if (pendingTripId) {
        const clickedTrip = data.find((t) => t.trip_id === pendingTripId);

        // If it's taken / status changed / not offerable anymore, release the click state
        if (!clickedTrip || clickedTrip.status !== "PENDING_OFFER") {
          setPendingTripId(null);
        }
      }

    } catch (e) {
      console.error("Error fetching orders:", e);
      const message = getErrorMessage(e);
      openToast(message || "An error occurred while fetching orders", 5000, "danger");
      setOrders([]);
      navigate("/driver");
    }
  }, [navigate, openToast, pendingTripId]);

  // Subscribe to push notifications on component mount if not already subscribed
  useEffect(() => {
    if (receiveNotifications) {
      subscribeToPush();
    }
  }, [receiveNotifications]);

  useEffect(() => {
    // Get the orders once then keep fetching and updating every 5 seconds
    reloadOrders();
    const intervalCall = setInterval(() => {
      reloadOrders();
    }, 5000);
    return () => {
      // clean up
      clearInterval(intervalCall);
    };
  }, [reloadOrders]);

  useEffect(() => {
    if (tripMessage?.type === "offer_made") {
      const offerData = tripMessage.data;
      navigate(`/currentOrders/${offerData.trip_id}`);
    }
    else if (tripMessage?.type === "error_occurred") {
      const errorMessages = tripMessage.data;
      console.error("Error occurred:", errorMessages);

      openToast(errorMessages[0], 5000, "danger");
    }
  }, [tripMessage, openToast, navigate]);

  const handleSendJsonMessage = (tripId, priceOffer) => {
    sendTripMessage({
      event: "make_offer",
      data: JSON.stringify({
        trip_id: tripId,
        price_offer: priceOffer,
      }),
    });
  };

  const getMaxAdjustment = (trip) => {
    const originalPrice = Number(trip.price_offer);
    if (trip.is_within_campus) {
      return originalPrice >= 5 ? 1 : 0;
    } else {
      if (originalPrice >= 10) return 3;
      if (originalPrice >= 5) return 2;
      if (originalPrice >= 3) return 1;
    }
    return 0;
  }

  const handleAdjustPrice = (e, tripId, amount, maxAdj) => {
    e.stopPropagation();
    setPriceAdjustments(prev => {
      const currentAdj = prev[tripId] ?? 0;
      const newAdj = currentAdj + amount;

      if (newAdj < 0 || newAdj > maxAdj) return prev;

      return { ...prev, [tripId]: newAdj };
    });
  };

  const getAdjustedPrice = (trip) => {
    const adjustment = priceAdjustments[trip.trip_id] ?? 0;
    return Number(trip.price_offer) + adjustment;
  };

  const handleSelect = (trip) => {
    setPendingTripId(trip.trip_id);
    const adjustment = priceAdjustments[trip.trip_id] ?? 0;
    const finalPriceOffer = Number(trip.price_offer) + adjustment;
    handleSendJsonMessage(trip.trip_id, finalPriceOffer);
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleReceiveNotificationsToggle = useCallback(async () => {
    const newValue = !receiveNotifications;

    try {
      await driverApi.updateDriverInfo({
        receive_notifications: newValue
      });

      // Only update state if API call was successful
      setReceiveNotifications(newValue);
    } catch (error) {
      console.error("Error updating notifications:", error);
      const message = getErrorMessage(error);
      openToast(message || "Failed to update notification preference", 5000, "danger");
    }
  }, [receiveNotifications, openToast]);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.backArrow} onClick={() => navigate("/driver")}>
          <img
            className={classes.backArrowIcon}
            src={backArrow || "/placeholder.svg"}
            alt="back arrow"
          />
        </div>

        {/* Toggle to recieve notifications */}
        {orders && <Toggle isOn={receiveNotifications}
          handleToggle={handleReceiveNotificationsToggle}
          label={"Receive Notifications?"}
          className={classes.notificationToggle}
        />}

        {(!orders || orders.length === 0) && (
          <div className={classes.emptyOrderList}>
            <p>No orders found! Please wait...</p>
          </div>
        )}
        {orders && orders.length > 0 && (
          <div className={classes.orderList}>
            {[...orders]
              .sort((a, b) => {
                // i hate mohammed

                // Priority:
                // 1. Latest untaken
                // 2. Untaken (rest)
                // 3. Taken latest
                // 4. Taken (rest)

                const isUntakenA = a.status === "PENDING_OFFER";
                const isUntakenB = b.status === "PENDING_OFFER";

                // If one is untaken and the other is not, untaken comes first
                if (isUntakenA !== isUntakenB) {
                  return isUntakenA ? -1 : 1;
                }

                // Otherwise sort by create time descending (latest first)
                const dateA = new Date(a.created_time);
                const dateB = new Date(b.created_time);
                return dateB - dateA;
              })
              .map((row) => {
                const isClickedPending = pendingTripId === row.trip_id;
                return (
                  <InfoCard
                    className={`${classes.infoCard} ${isClickedPending ? classes.pendingCard : ""}`}
                    title={infoCardTitle}
                    time={formatTime(row.created_time)}
                    variant="orders"
                    onClick={() => handleSelect(row)}
                    isFemaleDriver={row.request_female}
                    disabled={isClickedPending || (row.request_female && data.gender === "M") || row.status !== "PENDING_OFFER" || data.credit <= 0}
                  >
                    {isClickedPending && (
                      <div className={classes.spinnerOverlay}>
                        <div className={classes.spinner} />
                      </div>
                    )}

                    <div className={classes.infoCardContainer}>
                      <div className={classes.locationsImage}>
                        <RouteConnector />
                      </div>
                      <div className={classes.mainContent}>
                        {/* Top Row */}
                        <div className={classes.row}>
                          <p className={classes.locationAddress}>{row.from_location}</p>
                          <p className={classes.pax}>{row.have_luggage && <Luggage size={20} />} PAX {row.pax}</p>
                        </div>

                        <hr className={classes.separator} />

                        {/* Bottom Row */}
                        <div className={classes.row}>
                          <p className={classes.locationAddress}>{row.to_location}</p>

                          <div className={classes.priceContainer}>
                            <p className={classes.price}>RM {getAdjustedPrice(row).toFixed(2)}</p>

                            {row.is_peak_hour && row.status === "PENDING_OFFER" && getMaxAdjustment(row) > 0 && (
                              <div className={classes.btnGroup} onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className={`${classes.adjustBtn} ${classes.minusBtn} ${(priceAdjustments[row.trip_id] ?? 0) <= 0 ? classes.disabledBtn : ""}`} 
                                  onClick={(e) => handleAdjustPrice(e, row.trip_id, -1, getMaxAdjustment(row))}
                                >
                                  –
                                </button>
                                <button 
                                  className={`${classes.adjustBtn} ${classes.plusBtn} ${(priceAdjustments[row.trip_id] ?? 0) >= getMaxAdjustment(row) ? classes.disabledBtn : ""}`} 
                                  onClick={(e) => handleAdjustPrice(e, row.trip_id, 1, getMaxAdjustment(row))}
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </InfoCard>
                );
              })}
          </div>
        )}
      </div>
    </>
  );
};

export default DriverOrders;
